import { useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isDocumentedImageSize } from "@tandem/image-studio-shared";
import { api, ApiClientError } from "../api.js";
import { useAuth } from "../auth.js";
import {
  clearGenerationDraft,
  readGenerationDraft,
  saveGenerationDraft,
  type GenerationDraft,
} from "../generation-draft.js";
import {
  GenerationPromptPreview,
  type PromptPreviewState,
} from "../components/generation-prompt-preview.js";
import "../components/generation-prompt-preview.css";
import "../create-free-mode.css";
import { aspectRatio } from "../ui.js";
import type {
  BrandAsset,
  Client,
  GenerationPreview,
  GenerationCapabilities,
  GenerationOutputFormat,
  GenerationQuality,
  GenerationReference,
  PlatformPreset,
  PromptTemplate,
  PromptTemplateVersion,
  TemplateVariable,
} from "../types.js";

type GenerationMode = "client_template" | "free_prompt";
type VariableValue = string | number | boolean;
type AssetGroup = { label: string; items: BrandAsset[] };

const assetTypeLabels: Record<BrandAsset["type"], string> = {
  logo: "Логотипы",
  product: "Продукты",
  reference: "Референсы",
  style_reference: "Стилевые референсы",
  background: "Фоны",
  person: "Люди",
  other: "Другие материалы",
};

function findBriefVariable(variables: TemplateVariable[]) {
  const candidates = variables.filter(
    (variable) =>
      variable.source === "user" &&
      (variable.type === "string" || variable.type === "multiline_text"),
  );
  return (
    candidates.find((variable) => /^(user_)?brief$/i.test(variable.key)) ??
    candidates.find((variable) =>
      /бриф|задач|изображ|сюжет|brief|image|story/i.test(
        [variable.key, variable.label, variable.description].join(" "),
      ),
    ) ??
    candidates.find((variable) => variable.required) ??
    null
  );
}

function initialVariableValues(version: PromptTemplateVersion) {
  return Object.fromEntries(
    version.variablesSnapshot
      .filter((variable) => variable.source === "user")
      .flatMap((variable) => {
        if (variable.defaultValue !== null)
          return [[variable.key, variable.defaultValue]];
        return variable.type === "boolean" ? [[variable.key, false]] : [];
      }),
  ) as Record<string, VariableValue>;
}

function groupAssets(assets: BrandAsset[]) {
  const order: BrandAsset["type"][] = [
    "logo",
    "product",
    "style_reference",
    "reference",
    "background",
    "person",
    "other",
  ];
  return order.flatMap((type) => {
    const items = assets.filter((asset) => asset.type === type);
    return items.length ? [{ label: assetTypeLabels[type], items }] : [];
  }) satisfies AssetGroup[];
}

function imageCountLabel(count: number) {
  if (count % 10 === 1 && count % 100 !== 11) return "изображение";
  if (
    count % 10 >= 2 &&
    count % 10 <= 4 &&
    (count % 100 < 12 || count % 100 > 14)
  )
    return "изображения";
  return "изображений";
}

function qualityHint(quality: string) {
  if (quality === "draft") return "Быстрый результат для чернового просмотра.";
  if (quality === "maximum") return "Максимальная детализация итогового файла.";
  return "Оптимальный баланс качества и скорости.";
}

const qualityLabels: Record<GenerationQuality, string> = {
  draft: "Черновик",
  standard: "Стандартное",
  maximum: "Максимальное",
};

const formatLabels: Record<GenerationOutputFormat, string> = {
  png: "PNG",
  jpeg: "JPEG",
  webp: "WebP",
};

const DEFAULT_USER_BRIEF_MAX_LENGTH = 15_000;
const DEFAULT_FREE_PROMPT_MAX_LENGTH = 20_000;
const LEGACY_CUSTOM_PLATFORM_SLUG = "custom";

function formatHint(format: string) {
  if (format === "jpeg") return "Компактный файл без прозрачности.";
  if (format === "webp")
    return "Современный формат для публикации в интернете.";
  return "Для веба и презентаций, без потери качества.";
}

function aspectRatioOptionLabel(platform: PlatformPreset) {
  return `${aspectRatio(platform)} · ${platform.width} × ${platform.height} — ${platform.name}`;
}

function previewErrorMessage(code: string) {
  if (code === "GENERATION_QUALITY_UNAVAILABLE")
    return "Выберите доступный уровень качества.";
  if (code === "GENERATION_OUTPUT_FORMAT_UNAVAILABLE")
    return "Выберите доступный формат файла.";
  if (code === "GENERATION_VARIABLES_INVALID")
    return "Заполните обязательные параметры шаблона.";
  if (code.includes("REFERENCE")) return "Проверьте загруженные референсы.";
  if (code.includes("ASSET")) return "Проверьте выбранные материалы бренда.";
  if (code.includes("PLATFORM")) return "Выберите доступную площадку.";
  return "Проверьте параметры формы и повторите запрос.";
}

function VariableField({
  variable,
  value,
  onChange,
}: {
  variable: TemplateVariable;
  value: VariableValue | undefined;
  onChange: (value: VariableValue) => void;
}) {
  const label = (
    <span className="field-label">
      {variable.label}
      {variable.required && <b aria-hidden="true">*</b>}
    </span>
  );
  if (variable.type === "boolean") {
    return (
      <label className="variable-switch">
        <span>
          {label}
          {variable.description && <small>{variable.description}</small>}
        </span>
        <span className="switch-control">
          <input
            aria-label={variable.label}
            type="checkbox"
            checked={value === true}
            onChange={(event) => onChange(event.target.checked)}
          />
          <span aria-hidden="true" />
        </span>
      </label>
    );
  }
  if (variable.type === "enum") {
    return (
      <label>
        {label}
        <select
          aria-label={variable.label}
          required={variable.required}
          value={String(value ?? "")}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">Выберите значение</option>
          {variable.enumOptions.map((option) => (
            <option key={option}>{option}</option>
          ))}
        </select>
        {variable.description && <small>{variable.description}</small>}
      </label>
    );
  }
  if (variable.type === "multiline_text") {
    return (
      <label className="variable-wide">
        {label}
        <textarea
          aria-label={variable.label}
          rows={3}
          required={variable.required}
          value={String(value ?? "")}
          placeholder={variable.description}
          onChange={(event) => onChange(event.target.value)}
        />
      </label>
    );
  }
  return (
    <label>
      {label}
      <input
        aria-label={variable.label}
        type={variable.type === "integer" ? "number" : "text"}
        required={variable.required}
        value={String(value ?? "")}
        placeholder={variable.description}
        onChange={(event) =>
          onChange(
            variable.type === "integer"
              ? Number(event.target.value)
              : event.target.value,
          )
        }
      />
    </label>
  );
}

export function CreateScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [hydrated, setHydrated] = useState(false);
  const [draftStatus, setDraftStatus] = useState("");
  const [restoreNotice, setRestoreNotice] = useState("");
  const [seoQuery, setSeoQuery] = useState("");
  const [useBrandProfile, setUseBrandProfile] = useState(true);
  const skipClientLoad = useRef("");
  const skipTemplateLoad = useRef("");
  const detachedReferences = useRef(new Set<string>());
  const submitted = useRef(false);
  const sourceConsumed = useRef(false);
  const [mode, setMode] = useState<GenerationMode>("client_template");
  const [clients, setClients] = useState<Client[]>([]);
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [platforms, setPlatforms] = useState<PlatformPreset[]>([]);
  const [assets, setAssets] = useState<BrandAsset[]>([]);
  const [references, setReferences] = useState<GenerationReference[]>([]);
  const [clientId, setClientId] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [platformId, setPlatformId] = useState("");
  const [version, setVersion] = useState<PromptTemplateVersion | null>(null);
  const [values, setValues] = useState<Record<string, VariableValue>>({});
  const [selected, setSelected] = useState<string[]>([]);
  const [count, setCount] = useState(1);
  const [brief, setBrief] = useState("");
  const [instruction, setInstruction] = useState("");
  const [quality, setQuality] = useState<GenerationQuality>("maximum");
  const [capabilities, setCapabilities] =
    useState<GenerationCapabilities | null>(null);
  const [format, setFormat] = useState<GenerationOutputFormat>("png");
  const [transparent, setTransparent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState<GenerationPreview | null>(null);
  const [previewState, setPreviewState] = useState<PromptPreviewState>("idle");
  const [previewError, setPreviewError] = useState("");
  const [previewUpdatedAt, setPreviewUpdatedAt] = useState<number | null>(null);
  const previewSequence = useRef(0);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (sourceConsumed.current && !searchParams.get("from")) return;
    let cancelled = false;
    void Promise.all([
      api.listClients(
        new URLSearchParams({ status: "active", pageSize: "100" }),
      ),
      api.listTemplates(
        new URLSearchParams({ status: "published", pageSize: "100" }),
      ),
      api.listPlatformPresets(),
      api.getGenerationCapabilities(),
      api.getSettings().catch(() => null),
    ])
      .then(
        async ([
          clientResult,
          templateResult,
          platformResult,
          capabilityResult,
          studioSettings,
        ]) => {
          const supportedPlatforms = platformResult.items.filter(
            (platform) =>
              platform.slug !== LEGACY_CUSTOM_PLATFORM_SLUG &&
              isDocumentedImageSize(platform.width, platform.height),
          );
          let restored = user ? readGenerationDraft(user.id) : null;
          const from = searchParams.get("from");
          let notice = "";
          if (from) {
            restored = null;
            const source = await api.getGeneration(from);
            const data = source.reuseData;
            if (!data) throw new Error("REUSE_UNAVAILABLE");
            restored = {
              mode: source.mode ?? "client_template",
              clientId: source.clientId ?? "",
              templateId: source.templateId ?? "",
              platformId: source.platformSnapshot.id,
              count: source.requestedCount,
              brief: data.prompt ?? "",
              instruction: data.additionalInstruction,
              seoQuery: data.seoQuery,
              values: data.variableValues,
              selected: data.selectedAssetIds,
              references: data.references,
              quality: source.settings.quality as GenerationQuality,
              format: source.settings.outputFormat as GenerationOutputFormat,
              transparent: source.settings.transparentBackground,
              useBrandProfile: source.settings.useBrandProfile !== false,
            };
            notice =
              "Параметры скопированы. Новая генерация ещё не запущена. Для шаблона используются актуальная опубликованная версия и текущие данные клиента — проверьте итоговый запрос.";
            if (
              data.referenceCount > data.references.length ||
              data.assetCount > data.selectedAssetIds.length
            )
              notice +=
                " Часть прежних файлов недоступна и не перенесена. При необходимости загрузите их заново.";
          } else if (searchParams.get("clientId")) restored = null;
          else if (restored)
            notice =
              "Черновик восстановлен из этого браузера. Проверьте актуальные параметры и файлы.";
          if (restored) {
            if (!clientResult.items.some((c) => c.id === restored.clientId)) {
              if (restored.clientId)
                notice += " Прежний клиент недоступен — выберите клиента.";
              restored.clientId = "";
            }
            const selectedTemplate = templateResult.items.find(
              (t) =>
                t.id === restored.templateId &&
                (t.scope === "global" || t.clientId === restored.clientId),
            );
            let restoredVersion: PromptTemplateVersion | null = null;
            if (selectedTemplate?.currentVersionNumber)
              restoredVersion = await api.getTemplateVersion(
                selectedTemplate.id,
                selectedTemplate.currentVersionNumber,
              );
            else {
              if (restored.templateId)
                notice +=
                  " Прежний шаблон недоступен — выберите опубликованный шаблон.";
              restored.templateId = "";
            }
            const restoredAssets = restored.clientId
              ? (await api.listAssets(restored.clientId)).items
              : [];
            // Validate upload access again: drafts can outlive an archive or session.
            const checkedRefs = await Promise.all(
              restored.references.map(async (reference) => {
                try {
                  const response = await fetch(
                    `/api/v1/generation-references/${encodeURIComponent(reference.id)}/preview`,
                    { credentials: "include" },
                  );
                  return response.ok
                    ? {
                        ...reference,
                        previewUrl: `/api/v1/generation-references/${reference.id}/preview`,
                      }
                    : null;
                } catch {
                  return null;
                }
              }),
            );
            const validRefs = checkedRefs.filter(
              (r): r is GenerationReference => r !== null,
            );
            if (validRefs.length < restored.references.length)
              notice +=
                " Недоступные референсы не перенесены — загрузите их заново.";
            if (cancelled) return;
            skipClientLoad.current = restored.clientId;
            skipTemplateLoad.current = restored.templateId;
            setMode(restored.mode);
            setClientId(restored.clientId);
            setTemplateId(restored.templateId);
            setAssets(restoredAssets);
            setVersion(restoredVersion);
            setSelected(
              restored.selected.filter((id) =>
                restoredAssets.some(
                  (a) => a.id === id && a.status === "active",
                ),
              ),
            );
            setReferences(validRefs);
            detachedReferences.current = new Set(validRefs.map((r) => r.id));
            const userKeys = new Set(
              restoredVersion?.variablesSnapshot
                .filter((v) => v.source === "user")
                .map((v) => v.key) ?? [],
            );
            const restoredValues = Object.fromEntries(
              Object.entries(restored.values).filter(([key]) =>
                userKeys.has(key),
              ),
            );
            setValues(restoredValues);
            const detected = restoredVersion
              ? findBriefVariable(restoredVersion.variablesSnapshot)
              : null;
            setBrief(
              restored.mode === "client_template" && detected
                ? String(restoredValues[detected.key] ?? "")
                : restored.brief,
            );
            setInstruction(restored.instruction);
            setSeoQuery(restored.seoQuery);
            setCount(restored.count);
            setQuality(
              capabilityResult.qualities.includes(restored.quality)
                ? restored.quality
                : capabilityResult.defaultQuality,
            );
            setFormat(
              capabilityResult.outputFormats.includes(restored.format)
                ? restored.format
                : capabilityResult.defaultOutputFormat,
            );
            if (!supportedPlatforms.some((p) => p.id === restored.platformId))
              notice +=
                " Прежнее соотношение сторон недоступно — выберите новое.";
            setPlatformId(
              supportedPlatforms.some((p) => p.id === restored.platformId)
                ? restored.platformId
                : "",
            );
            setTransparent(false);
            setUseBrandProfile(restored.useBrandProfile);
          }
          if (cancelled) return;
          setRestoreNotice(notice);
          setClients(clientResult.items);
          setTemplates(templateResult.items);
          setPlatforms(supportedPlatforms);
          setCapabilities(capabilityResult);
          setQuality((current) =>
            capabilityResult.qualities.includes(current)
              ? current
              : capabilityResult.defaultQuality,
          );
          setFormat((current) =>
            capabilityResult.outputFormats.includes(current)
              ? current
              : capabilityResult.defaultOutputFormat,
          );
          if (!restored) {
            if (studioSettings) {
              setCount(studioSettings.defaultCount);
              setQuality(
                capabilityResult.qualities.includes(
                  studioSettings.defaultQuality,
                )
                  ? studioSettings.defaultQuality
                  : capabilityResult.defaultQuality,
              );
              setFormat(
                capabilityResult.outputFormats.includes(
                  studioSettings.defaultOutputFormat,
                )
                  ? studioSettings.defaultOutputFormat
                  : capabilityResult.defaultOutputFormat,
              );
            }
            setPlatformId(
              (current) =>
                (supportedPlatforms.some((platform) => platform.id === current)
                  ? current
                  : "") ||
                supportedPlatforms.find(
                  (p) => p.id === studioSettings?.defaultPlatformId,
                )?.id ||
                supportedPlatforms[0]?.id ||
                "",
            );
          }
          const requestedClientId = searchParams.get("clientId");
          if (
            requestedClientId &&
            clientResult.items.some((item) => item.id === requestedClientId)
          )
            setClientId(requestedClientId);
          setHydrated(true);
          if (from) {
            sourceConsumed.current = true;
            void navigate("/create", { replace: true });
          }
        },
      )
      .catch(() =>
        setError(
          "Не удалось загрузить доступные параметры генерации. Обновите страницу.",
        ),
      );
    return () => {
      cancelled = true;
    };
  }, [searchParams, user, navigate]);

  useEffect(() => {
    if (capabilities && !capabilities.qualities.includes(quality))
      setQuality(capabilities.defaultQuality);
  }, [capabilities, quality]);

  useEffect(() => {
    if (capabilities && !capabilities.outputFormats.includes(format))
      setFormat(capabilities.defaultOutputFormat);
  }, [capabilities, format]);

  useEffect(() => {
    if (!hydrated) return;
    if (clientId && skipClientLoad.current === clientId) {
      skipClientLoad.current = "";
      return;
    }
    let cancelled = false;
    setAssets([]);
    setSelected([]);
    if (clientId)
      void api
        .listAssets(clientId)
        .then((result) => {
          if (cancelled) return;
          setAssets(result.items);
          setSelected([]);
        })
        .catch(() => setError("Не удалось загрузить материалы клиента."));
    return () => {
      cancelled = true;
    };
  }, [clientId, hydrated]);

  useEffect(() => {
    if (!hydrated) return;
    if (templateId && skipTemplateLoad.current === templateId) {
      skipTemplateLoad.current = "";
      return;
    }
    let cancelled = false;
    setVersion(null);
    setValues({});
    const template = templates.find((item) => item.id === templateId);
    if (template?.currentVersionNumber)
      void api
        .getTemplateVersion(template.id, template.currentVersionNumber)
        .then((nextVersion) => {
          if (cancelled) return;
          setVersion(nextVersion);
          const initialValues = initialVariableValues(nextVersion);
          const detectedBrief = findBriefVariable(
            nextVersion.variablesSnapshot,
          );
          setValues(initialValues);
          setBrief(
            detectedBrief ? String(initialValues[detectedBrief.key] ?? "") : "",
          );
          setPlatformId(nextVersion.platformPresetId ?? "");
          setCount(nextVersion.generationDefaults.imageCount);
          setQuality(nextVersion.generationDefaults.quality);
        })
        .catch(() => setError("Не удалось загрузить шаблон."));
    return () => {
      cancelled = true;
    };
  }, [templateId, templates, hydrated]);

  useEffect(() => {
    if (!hydrated || !user || submitted.current) return;
    const draft: GenerationDraft = {
      mode,
      clientId,
      templateId,
      platformId,
      values,
      selected,
      references,
      count,
      brief,
      instruction,
      seoQuery,
      quality,
      format,
      transparent,
      useBrandProfile,
    };
    const save = () => {
      if (submitted.current) return;
      const saved = saveGenerationDraft(user.id, draft);
      setDraftStatus(
        saved
          ? "Черновик сохранён в этом браузере на 7 дней. При выходе из аккаунта он удаляется."
          : "Браузер не разрешил сохранить черновик.",
      );
    };
    save();
    window.addEventListener("pagehide", save);
    return () => {
      window.removeEventListener("pagehide", save);
    };
  }, [
    hydrated,
    user,
    mode,
    clientId,
    templateId,
    platformId,
    values,
    selected,
    references,
    count,
    brief,
    instruction,
    seoQuery,
    quality,
    format,
    transparent,
    useBrandProfile,
  ]);

  const availableTemplates = useMemo(
    () =>
      templates.filter(
        (template) =>
          template.scope === "global" || template.clientId === clientId,
      ),
    [templates, clientId],
  );
  const selectedTemplate = templates.find(
    (template) => template.id === templateId,
  );
  const isArticleTemplate =
    mode === "client_template" && selectedTemplate?.category === "article";
  const userVariables = useMemo(
    () =>
      (version?.variablesSnapshot ?? [])
        .filter((variable) => variable.source === "user")
        .sort((left, right) => left.displayOrder - right.displayOrder),
    [version],
  );
  const briefVariable = useMemo(
    () => findBriefVariable(userVariables),
    [userVariables],
  );
  const templateVariables = briefVariable
    ? userVariables.filter((variable) => variable.key !== briefVariable.key)
    : userVariables;
  const selectedPlatform = platforms.find(
    (platform) => platform.id === platformId,
  );
  const assetGroups = groupAssets(assets);
  const requiredVariablesReady = userVariables
    .filter((variable) => variable.required)
    .every((variable) => {
      const value = values[variable.key];
      return (
        typeof value === "boolean" || String(value ?? "").trim().length > 0
      );
    });
  const targetReady =
    mode === "free_prompt"
      ? Boolean(selectedPlatform && brief.trim())
      : Boolean(
          clientId &&
          templateId &&
          selectedPlatform &&
          requiredVariablesReady &&
          (!isArticleTemplate || briefVariable) &&
          brief.trim(),
        );
  const generationQualityReady = Boolean(
    capabilities?.qualities.includes(quality),
  );
  const generationOutputFormatReady = Boolean(
    capabilities?.outputFormats.includes(format),
  );
  const canSubmit =
    !busy &&
    !uploadBusy &&
    targetReady &&
    generationQualityReady &&
    generationOutputFormatReady;
  const transparencyAvailable = false;
  const briefMaxLength =
    mode === "free_prompt"
      ? (capabilities?.freePromptMaxLength ?? DEFAULT_FREE_PROMPT_MAX_LENGTH)
      : (capabilities?.userBriefMaxLength ?? DEFAULT_USER_BRIEF_MAX_LENGTH);

  const generationInput = useMemo(
    () =>
      mode === "free_prompt"
        ? {
            mode: "free_prompt",
            ...(seoQuery.trim() ? { seoQuery: seoQuery.trim() } : {}),
            prompt: brief,
            platformPresetId: platformId,
            count,
            additionalInstruction: instruction,
            ...(references.length
              ? {
                  referenceImageIds: references.map(
                    (reference) => reference.id,
                  ),
                }
              : {}),
            settings: {
              quality,
              outputFormat: format,
              transparentBackground: transparent,
            },
          }
        : {
            mode: "client_template",
            ...(seoQuery.trim() ? { seoQuery: seoQuery.trim() } : {}),
            clientId,
            templateId,
            platformPresetId: platformId,
            count,
            variableValues: values,
            additionalInstruction: briefVariable
              ? instruction
              : [brief, instruction].filter(Boolean).join("\n\n"),
            selectedAssetIds: selected,
            ...(references.length
              ? {
                  referenceImageIds: references.map(
                    (reference) => reference.id,
                  ),
                }
              : {}),
            settings: {
              quality,
              outputFormat: format,
              transparentBackground: transparent,
              useBrandProfile,
            },
          },
    [
      brief,
      briefVariable,
      clientId,
      count,
      format,
      instruction,
      mode,
      platformId,
      quality,
      references,
      selected,
      templateId,
      transparent,
      values,
      seoQuery,
      useBrandProfile,
    ],
  );

  useEffect(() => {
    const sequence = ++previewSequence.current;
    if (
      !targetReady ||
      !generationQualityReady ||
      !generationOutputFormatReady
    ) {
      setPreview(null);
      setPreviewState("idle");
      setPreviewError("");
      return;
    }
    const controller = new AbortController();
    setPreviewState("loading");
    setPreviewError("");
    const timer = window.setTimeout(
      () =>
        void api
          .previewGeneration(generationInput, controller.signal)
          .then((result) => {
            if (sequence !== previewSequence.current) return;
            setPreview(result);
            setPreviewState("ready");
            setPreviewUpdatedAt(Date.now());
          })
          .catch((caught: unknown) => {
            if (
              controller.signal.aborted ||
              (caught instanceof Error && caught.name === "AbortError") ||
              sequence !== previewSequence.current
            )
              return;
            setPreviewState("error");
            setPreviewError(
              caught instanceof ApiClientError
                ? previewErrorMessage(caught.code)
                : "Попробуйте изменить параметры или повторить запрос.",
            );
          }),
      550,
    );
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [
    generationInput,
    generationOutputFormatReady,
    generationQualityReady,
    targetReady,
  ]);

  function updateBrief(nextBrief: string) {
    setBrief(nextBrief);
    if (mode === "client_template" && briefVariable)
      setValues((current) => ({ ...current, [briefVariable.key]: nextBrief }));
  }

  async function uploadReferenceFiles(files: FileList | null) {
    if (!files?.length) return;
    setUploadBusy(true);
    setError("");
    try {
      for (const file of Array.from(files)) {
        if (!["image/png", "image/jpeg", "image/webp"].includes(file.type))
          throw new Error("Поддерживаются PNG, JPEG и WebP.");
        const uploaded = await api.uploadGenerationReference(file);
        setReferences((current) => [...current, uploaded]);
      }
    } catch (caught) {
      setError(
        caught instanceof ApiClientError
          ? `${caught.code}: ${caught.message}`
          : caught instanceof Error
            ? caught.message
            : "Не удалось загрузить референс.",
      );
    } finally {
      setUploadBusy(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  async function removeReference(reference: GenerationReference) {
    setError("");
    try {
      if (!detachedReferences.current.has(reference.id))
        await api.archiveGenerationReference(reference.id);
      setReferences((current) =>
        current.filter((item) => item.id !== reference.id),
      );
    } catch (caught) {
      setError(
        caught instanceof ApiClientError
          ? `${caught.code}: ${caught.message}`
          : "Не удалось удалить референс.",
      );
    }
  }

  async function submit() {
    setBusy(true);
    setError("");
    try {
      const batch = await api.createGeneration(generationInput);
      submitted.current = true;
      if (user) clearGenerationDraft(user.id);
      void navigate(`/generations/${batch.id}`);
    } catch (caught) {
      setError(
        caught instanceof ApiClientError
          ? `${caught.code}: ${caught.message}`
          : "Не удалось создать генерацию.",
      );
      setBusy(false);
    }
  }

  return (
    <div className="create-page">
      <header className="create-page-heading">
        <div>
          <h1>Создать изображение</h1>
          <p>
            Создавайте изображения по правилам клиента или только по
            собственному запросу.
          </p>
        </div>
      </header>

      {restoreNotice && (
        <div className="notice" role="status">
          {restoreNotice}
        </div>
      )}
      {draftStatus && (
        <div className="create-draft-status">
          <small>{draftStatus}</small>
          <button
            type="button"
            className="secondary compact-button"
            onClick={() => {
              if (
                !window.confirm(
                  "Очистить черновик и начать заново? Загруженные файлы и история генераций сохранятся.",
                )
              )
                return;
              submitted.current = true;
              if (user) clearGenerationDraft(user.id);
              window.location.assign("/create");
            }}
          >
            Очистить черновик
          </button>
        </div>
      )}

      <div
        className="generation-mode-switch"
        role="tablist"
        aria-label="Режим генерации"
      >
        <button
          type="button"
          role="tab"
          aria-selected={mode === "client_template"}
          className={mode === "client_template" ? "active" : ""}
          onClick={() => setMode("client_template")}
        >
          По шаблону клиента
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "free_prompt"}
          className={mode === "free_prompt" ? "active" : ""}
          onClick={() => setMode("free_prompt")}
        >
          ✦ Свободная генерация
        </button>
      </div>

      <div className="generation-create-layout">
        <section className="panel generation-form">
          {error && <div className="error-banner">{error}</div>}
          <label className="seo-query-field">
            <span className="field-label">Тема статьи / SEO-запрос</span>
            <input
              aria-label="Тема статьи / SEO-запрос"
              maxLength={240}
              value={seoQuery}
              onChange={(event) => setSeoQuery(event.target.value)}
              placeholder="Например: ремонт отопления в коммерческом помещении"
            />
            <small>
              Используется для имени файла, не добавляется к запросу модели.
            </small>
            <small aria-live="polite">
              Имя файла:{" "}
              {previewState === "ready" && preview?.downloadFilename
                ? preview.downloadFilename
                : "заполните запрос и дождитесь предпросмотра"}
            </small>
          </label>
          {mode === "client_template" && (
            <>
              <div className="generation-selectors">
                <label>
                  <span className="field-label">
                    Клиент <b aria-hidden="true">*</b>
                  </span>
                  <select
                    aria-label="Клиент"
                    value={clientId}
                    onChange={(event) => {
                      setClientId(event.target.value);
                      setTemplateId("");
                    }}
                  >
                    <option value="">Выберите клиента</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span className="field-label">
                    Шаблон <b aria-hidden="true">*</b>
                  </span>
                  <select
                    aria-label="Шаблон"
                    value={templateId}
                    disabled={!clientId}
                    onChange={(event) => setTemplateId(event.target.value)}
                  >
                    <option value="">Выберите опубликованный шаблон</option>
                    <optgroup label="Общие">
                      {availableTemplates
                        .filter((template) => template.scope === "global")
                        .map((template) => (
                          <option key={template.id} value={template.id}>
                            {template.name}
                          </option>
                        ))}
                    </optgroup>
                    <optgroup label="Для клиента">
                      {availableTemplates
                        .filter((template) => template.scope === "client")
                        .map((template) => (
                          <option key={template.id} value={template.id}>
                            {template.name}
                          </option>
                        ))}
                    </optgroup>
                  </select>
                </label>
              </div>
              <div className="create-divider" />
            </>
          )}

          <label
            className={`brief-field ${isArticleTemplate ? "article-brief-field" : ""}`}
          >
            <span className="field-label">
              {isArticleTemplate ? "Текст статьи" : "Что изображаем"}{" "}
              <b aria-hidden="true">*</b>
            </span>
            <span className="textarea-wrap">
              <textarea
                aria-label={
                  isArticleTemplate ? "Текст статьи" : "Что изображаем"
                }
                rows={isArticleTemplate ? 12 : 5}
                maxLength={briefMaxLength}
                required
                value={brief}
                onChange={(event) => updateBrief(event.target.value)}
                placeholder={
                  isArticleTemplate
                    ? "Вставьте текст статьи целиком. Шаблон сам определит главную тему, ключевые тезисы и структуру изображения."
                    : "Опишите сюжет, объекты, настроение и основную идею изображения."
                }
              />
              <small>
                {brief.length} / {briefMaxLength}
              </small>
            </span>
            {isArticleTemplate && (
              <small className="control-hint">
                Используйте исходный материал без сокращения. На изображение
                попадут только выбранные шаблоном ключевые тезисы.
              </small>
            )}
          </label>
          <label className="additional-instruction-field">
            Дополнительные инструкции (необязательно)
            <span className="textarea-wrap">
              <textarea
                aria-label="Дополнительные инструкции"
                rows={3}
                maxLength={10000}
                value={instruction}
                onChange={(event) => setInstruction(event.target.value)}
                placeholder="Уточните композицию, текст, цвета, ограничения и важные детали."
              />
              <small>{instruction.length} / 10000</small>
            </span>
          </label>

          <ReferenceUpload
            references={references}
            busy={uploadBusy}
            fileInput={fileInput}
            mode={mode}
            onFiles={(files) => void uploadReferenceFiles(files)}
            onRemove={(reference) => void removeReference(reference)}
          />

          {mode === "client_template" &&
            version &&
            templateVariables.length > 0 && (
              <section className="generation-variables">
                <div className="subsection-heading">
                  <div>
                    <h2>Переменные шаблона</h2>
                    <p>Значения будут подставлены в шаблон перед созданием.</p>
                  </div>
                </div>
                <div className="variable-grid">
                  {templateVariables.map((variable) => (
                    <VariableField
                      key={variable.key}
                      variable={variable}
                      value={values[variable.key]}
                      onChange={(value) =>
                        setValues((current) => ({
                          ...current,
                          [variable.key]: value,
                        }))
                      }
                    />
                  ))}
                </div>
              </section>
            )}

          <GenerationPromptPreview
            preview={preview}
            state={previewState}
            error={previewError}
            updatedAt={previewUpdatedAt}
            mode={mode}
          />

          <footer className="sticky-generate">
            <div>
              <small>Будет создано</small>
              <strong>
                {count} {imageCountLabel(count)}
              </strong>
            </div>
            <span className="generation-action">
              {!canSubmit && !busy && (
                <small>Заполните обязательные поля</small>
              )}
              <button
                className="primary generate-button"
                disabled={!canSubmit}
                onClick={() => void submit()}
              >
                {busy
                  ? "Создаём…"
                  : `Создать ${count} ${imageCountLabel(count)}`}
              </button>
            </span>
          </footer>
        </section>

        <aside className="panel generation-settings">
          <div className="settings-heading">
            <h2>Параметры изображения</h2>
            <span aria-hidden="true">☷</span>
          </div>
          <label>
            Соотношение сторон
            <select
              aria-label="Соотношение сторон"
              value={platformId}
              onChange={(event) => {
                const platform = platforms.find(
                  (item) => item.id === event.target.value,
                );
                setPlatformId(event.target.value);
                if (platform)
                  setFormat(
                    capabilities?.outputFormats.includes(
                      platform.outputFormatDefault,
                    )
                      ? platform.outputFormatDefault
                      : (capabilities?.defaultOutputFormat ?? "png"),
                  );
              }}
            >
              <option value="">Выберите размер и соотношение сторон</option>
              {platforms.map((platform) => (
                <option key={platform.id} value={platform.id}>
                  {aspectRatioOptionLabel(platform)}
                </option>
              ))}
            </select>
            <small className="control-hint">
              {selectedPlatform
                ? `${aspectRatio(selectedPlatform)} · ${selectedPlatform.width} × ${selectedPlatform.height} px. ${mode === "client_template" && version ? "Площадка и рекомендуемый формат сохранены в шаблоне." : "Выберите подходящий кадр."}`
                : "Выберите соотношение сторон итогового изображения."}
            </small>
          </label>
          <div className="settings-pair">
            <label>
              Количество
              <input
                aria-label="Количество"
                type="number"
                min="1"
                max="20"
                value={count}
                onChange={(event) =>
                  setCount(
                    Math.max(1, Math.min(20, Number(event.target.value))),
                  )
                }
              />
            </label>
            <label>
              Качество
              <select
                aria-label="Качество"
                value={quality}
                disabled={!capabilities}
                onChange={(event) =>
                  setQuality(event.target.value as GenerationQuality)
                }
              >
                {capabilities?.qualities.map((availableQuality) => (
                  <option key={availableQuality} value={availableQuality}>
                    {qualityLabels[availableQuality]}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="control-hint">{qualityHint(quality)}</p>
          <label>
            Формат
            <select
              aria-label="Формат"
              value={format}
              disabled={!capabilities}
              onChange={(event) => {
                setFormat(event.target.value as GenerationOutputFormat);
                if (event.target.value === "jpeg") setTransparent(false);
              }}
            >
              {capabilities?.outputFormats.map((availableFormat) => (
                <option key={availableFormat} value={availableFormat}>
                  {formatLabels[availableFormat]}
                </option>
              ))}
            </select>
            <small className="control-hint">{formatHint(format)}</small>
          </label>
          <label className="switch-setting">
            <span>
              Прозрачный фон
              <small>
                {transparencyAvailable
                  ? "Фон изображения будет прозрачным."
                  : "Недоступно для текущего способа генерации."}
              </small>
            </span>
            <span className="switch-control">
              <input
                aria-label="Прозрачный фон"
                type="checkbox"
                checked={transparent}
                disabled={!transparencyAvailable || format === "jpeg"}
                onChange={(event) => setTransparent(event.target.checked)}
              />
              <span aria-hidden="true" />
            </span>
          </label>
          <div className="settings-divider" />

          {mode === "free_prompt" ? (
            <FreeReferenceSummary references={references} />
          ) : (
            <section className="brand-assets">
              <div className="subsection-heading">
                <div>
                  <h2>Материалы бренда</h2>
                  <p>Выберите референсы для изображения.</p>
                </div>
                <span className="badge">
                  {preview
                    ? `${preview.brandAssets.selected.length + preview.brandAssets.automaticallySelected.length} используется`
                    : `${selected.length} вручную`}
                </span>
              </div>
              {!clientId ? (
                <div className="brand-assets-empty">
                  <span aria-hidden="true">▱</span>
                  <strong>Сначала выберите клиента</strong>
                  <p>Здесь появятся логотипы, продукты и референсы.</p>
                </div>
              ) : assets.length === 0 ? (
                <div className="brand-assets-empty">
                  <span aria-hidden="true">▱</span>
                  <strong>Материалы не добавлены</strong>
                  <p>Изображение можно создать без брендовых файлов.</p>
                </div>
              ) : (
                <div className="asset-groups">
                  {assetGroups.map((group) => (
                    <div className="asset-group" key={group.label}>
                      <h3>{group.label}</h3>
                      <div className="asset-thumbnail-grid">
                        {group.items.map((asset) => (
                          <label
                            key={asset.id}
                            className={
                              selected.includes(asset.id) ? "selected" : ""
                            }
                          >
                            <input
                              type="checkbox"
                              checked={selected.includes(asset.id)}
                              onChange={(event) =>
                                setSelected((current) =>
                                  event.target.checked
                                    ? [...current, asset.id]
                                    : current.filter((id) => id !== asset.id),
                                )
                              }
                            />
                            <img src={asset.previewUrl} alt="" />
                            <span>
                              <strong>{asset.title}</strong>
                              {asset.preferredForGeneration && (
                                <small>
                                  {selected.includes(asset.id)
                                    ? "Выбран вручную"
                                    : "Будет выбран автоматически"}
                                </small>
                              )}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}

function ReferenceUpload({
  references,
  busy,
  fileInput,
  mode,
  onFiles,
  onRemove,
}: {
  references: GenerationReference[];
  busy: boolean;
  fileInput: RefObject<HTMLInputElement | null>;
  mode: GenerationMode;
  onFiles: (files: FileList | null) => void;
  onRemove: (reference: GenerationReference) => void;
}) {
  return (
    <section className="free-reference-upload">
      <div className="subsection-heading">
        <div>
          <h2>
            {mode === "client_template" ? "Дополнительные файлы" : "Референсы"}
          </h2>
          <p>
            {mode === "client_template"
              ? "Добавьте свои изображения к шаблону и материалам клиента."
              : "Изображения будут переданы модели как визуальные ориентиры."}
          </p>
        </div>
        <button
          type="button"
          className="secondary compact"
          disabled={busy}
          onClick={() => fileInput.current?.click()}
        >
          ↥ {busy ? "Загружаем…" : "Загрузить изображения"}
        </button>
      </div>
      <input
        ref={fileInput}
        className="visually-hidden"
        aria-label="Загрузить изображения-референсы"
        type="file"
        accept="image/png,image/jpeg,image/webp"
        multiple
        onChange={(event) => onFiles(event.target.files)}
      />
      <button
        type="button"
        className="reference-dropzone"
        onClick={() => fileInput.current?.click()}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          onFiles(event.dataTransfer.files);
        }}
      >
        <strong>Перетащите изображения сюда</strong>
        <span>или нажмите для выбора файлов</span>
        <small>
          PNG, JPEG, WebP · ограничения проекта применяются на сервере
        </small>
      </button>
      {references.length > 0 && (
        <div className="reference-thumbnail-list">
          {references.map((reference) => (
            <figure key={reference.id}>
              <img src={reference.previewUrl} alt="" />
              <figcaption title={reference.originalFilename}>
                {reference.originalFilename}
              </figcaption>
              <button
                type="button"
                aria-label={`Удалить ${reference.originalFilename}`}
                onClick={() => onRemove(reference)}
              >
                ×
              </button>
            </figure>
          ))}
        </div>
      )}
    </section>
  );
}

function FreeReferenceSummary({
  references,
}: {
  references: GenerationReference[];
}) {
  return (
    <section className="free-reference-summary">
      <div className="subsection-heading">
        <div>
          <h2>Изображения-референсы</h2>
          <p>
            {references.length
              ? `${references.length} ${imageCountLabel(references.length)} будет передано модели.`
              : "Референсы не загружены."}
          </p>
        </div>
        {references.length > 0 && (
          <span className="badge">{references.length}</span>
        )}
      </div>
      {references.length > 0 && (
        <div className="reference-summary-grid">
          {references.map((reference) => (
            <img
              key={reference.id}
              src={reference.previewUrl}
              alt=""
              title={reference.originalFilename}
            />
          ))}
        </div>
      )}
      <div className="free-mode-notice">
        <strong>ⓘ Подстановка данных клиента отключена</strong>
        <p>
          Используются только ваш запрос, выбранные параметры и загруженные
          референсы.
        </p>
      </div>
    </section>
  );
}
